/**
 * SpneDesignPage — Anabiya Decor × SPNE Design partnership page.
 * Editorial single-page presentation. Copy overridable via home_content.spne_design section
 * (added to backend SECTION_KEYS below).
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Palette, Compass, Sparkles, Handshake } from "lucide-react";
import { NavbarLuxe } from "@/components/NavbarLuxe";
import { AnnouncementBar } from "@/components/AnnouncementBar";
import { Footer } from "@/components/Footer";
import { FloatingButtons } from "@/components/FloatingButtons";
import { Seo } from "@/components/Seo";
import { useHomeContent } from "@/context/HomeContentContext";
import { useSmoothScroll } from "@/hooks/useSmoothScroll";

const DEFAULTS = {
  kicker: "DESIGN PARTNERSHIP",
  headline: "SPNE",
  headline_accent: " Design.",
  subtitle: "Creative Design Partner to Anabiya Decor",
  intro:
    "Anabiya Decor's collaboration with SPNE Design brings a thoughtful contemporary perspective to the way we create, curate and present our textile collections. Together we shape considered pieces designed for beautiful living.",
  hero_image:
    "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1600&q=80",
  sections: [
    {
      icon: "Palette",
      title: "About SPNE Design",
      body:
        "SPNE Design is a creative studio focused on refined, editorial design across textile, brand and interior. As Anabiya Decor's design collaborator, SPNE brings a modern sensibility to every collection.",
    },
    {
      icon: "Compass",
      title: "Design Philosophy",
      body:
        "Timeless proportions, honest materials and quiet detail — every piece is designed to feel restful, considered and enduring, rather than trend-driven.",
    },
    {
      icon: "Sparkles",
      title: "Creative Direction",
      body:
        "From colour palettes to embroidery motifs, SPNE shapes the visual language of Anabiya Decor — cohesive across bedding, cushions, curtains and gifting.",
    },
    {
      icon: "Handshake",
      title: "Collaboration with Anabiya Decor",
      body:
        "Anabiya Decor brings craftsmanship, textile heritage and manufacturing excellence in Mumbai. SPNE brings creative direction. Together, we create considered textiles for beautifully considered spaces.",
    },
  ],
};

const ICONS = { Palette, Compass, Sparkles, Handshake };

const SpneDesignPage = () => {
  useSmoothScroll();
  const { getSection } = useHomeContent();
  const cfg = getSection("spne_design", DEFAULTS);
  const sections = Array.isArray(cfg.sections) && cfg.sections.length ? cfg.sections : DEFAULTS.sections;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main data-testid="spne-page" className="bg-white text-forest min-h-screen">
      <Seo
        path="/spne-design"
        title="SPNE Design — Creative Design Partner to Anabiya Decor"
        description="Anabiya Decor × SPNE Design — a creative partnership shaping refined textiles, editorial direction and considered luxury for beautiful living."
      />
      <AnnouncementBar />
      <NavbarLuxe />

      {/* Hero */}
      <section className="pt-24 md:pt-32 pb-14 md:pb-20 px-6 md:px-14 lg:px-20 bg-cream border-b border-hairline">
        <div className="max-w-6xl mx-auto">
          <div className="overline text-gold tracking-[4px] text-[11px] mb-5" data-testid="spne-kicker">
            {cfg.kicker || DEFAULTS.kicker}
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.7, 0, 0.2, 1] }}
            className="font-display text-forest text-6xl md:text-8xl lg:text-9xl leading-[0.9] tracking-[-0.02em]"
            data-testid="spne-h1"
          >
            {cfg.headline || DEFAULTS.headline}
            <span className="font-editorial italic text-gold">{cfg.headline_accent || DEFAULTS.headline_accent}</span>
          </motion.h1>
          <p className="mt-6 text-forest/70 text-xl md:text-2xl font-editorial italic" data-testid="spne-subtitle">
            {cfg.subtitle || DEFAULTS.subtitle}
          </p>
          <p className="mt-8 max-w-3xl text-forest/70 text-base md:text-lg leading-relaxed font-light">
            {cfg.intro || DEFAULTS.intro}
          </p>
        </div>
      </section>

      {/* Hero image */}
      <section className="px-6 md:px-14 lg:px-20 py-10">
        <div className="max-w-6xl mx-auto aspect-[16/7] overflow-hidden bg-beige">
          <img src={cfg.hero_image || DEFAULTS.hero_image} alt="SPNE Design × Anabiya Decor — creative direction" loading="lazy" className="w-full h-full object-cover" />
        </div>
      </section>

      {/* Sections grid */}
      <section className="py-16 md:py-24 px-6 md:px-14 lg:px-20 bg-white">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14">
          {sections.slice(0, 4).map((s, i) => {
            const Icon = ICONS[s.icon] || Palette;
            return (
              <div key={i} data-testid={`spne-section-${i}`} className="flex flex-col">
                <Icon size={28} strokeWidth={1.4} className="text-gold mb-5" />
                <h2 className="font-display text-forest text-3xl md:text-4xl leading-tight tracking-tight">
                  {s.title}
                </h2>
                <p className="mt-4 text-forest/70 text-base leading-relaxed font-light">{s.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 px-6 md:px-14 lg:px-20 bg-forest text-champagne">
        <div className="max-w-4xl mx-auto text-center">
          <div className="overline text-gold tracking-[4px] text-[11px] mb-4">§ Discover the Collection</div>
          <h2 className="font-display text-champagne text-3xl md:text-5xl leading-tight">
            Considered textiles for<span className="italic font-editorial text-gold"> beautifully considered spaces.</span>
          </h2>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link
              to="/shop"
              data-testid="spne-cta-shop"
              className="inline-flex items-center gap-3 bg-gold text-forest overline text-xs px-8 py-4 hover:bg-champagne transition-colors duration-500 tracking-[3px]"
            >
              Shop Collection <ArrowRight size={13} strokeWidth={1.8} />
            </Link>
            <Link
              to="/about"
              data-testid="spne-cta-about"
              className="inline-flex items-center gap-3 border border-champagne/60 text-champagne overline text-xs px-8 py-4 hover:bg-champagne hover:text-forest transition-colors duration-500 tracking-[3px]"
            >
              Our Story <ArrowRight size={13} strokeWidth={1.8} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </main>
  );
};

export default SpneDesignPage;
